import React, { Fragment, useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/configureStore';
import { AGENDA_SET_STATUS_LIST } from '../../store/actions/ActionTypes';
import { Box, Button, IconButton, Tab, Tabs } from '@material-ui/core';
import { Card } from 'reactstrap';
// Components
import { getAppNameColor, getStatusList } from '../../utils/appointments';
import { AppointmentHistoryType, AppointmentStatusType, AppointmentType } from '../../types/Agenda';
import { getAppointmentDetailsRequest, getAppointmentAttachments } from '../agenda/agenda.requests';
import { useHistory, useParams } from 'react-router';
import Loader from '../Loader';
import { TranslatorService } from '../../services/TranslatorService';
import { PageTitle } from '../../layout-components';
import TabPanel from '../_shared/Tabs';
import { AppUserService } from '../../services/AppUserService';
import { useSnackbar } from 'notistack';
import AppointmentDetails from './AppointmentDetails';
import AppointmentHistory from './AppointmentHistory';
import { CreateNewFolder, Folder } from '@material-ui/icons';
import { NewCaseAppointment } from '../agenda/NewCaseAppointment';
import AppointmentTabDocumentsForm from './AppointmentTabDocumentsForm';
import { AppContext, ApplicationContext } from '../../context/Contexts';
import { Subject, takeUntil } from 'rxjs';
import { CaseAttachment } from '../../interfaces/Case';
import { AttachmentTypeCode } from '../../helpers/Constants';
import { Organization } from '../../interfaces/Organization';

const AppointmentPage = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const { id } = useParams();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const translatorService = new TranslatorService();
  const statusList = useAppSelector((state) => state.agenda?.statusList);
  const [appointment, setAppointment] = React.useState<AppointmentType>();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [appointmentHistory, setAppointmentHistory] = useState<Array<AppointmentHistoryType>>();
  const [appointmentId, setAppointmentId] = useState<number | null>(null);
  const viewPartnerAppointments = useAppSelector(
    (state) => state.app?.appUser?.organization?.viewOwnerEntries ?? false
  );
  const organizationId = useAppSelector((state) => state.app?.appUser?.organization?.id);
  const context = useContext(ApplicationContext);
  const refreshService = context.refreshService;
  const organizationService = context.organizationService;
  const destroy$: Subject<void> = new Subject<void>();
  const apptIdRef = useRef<number | null>(null);
  const attachmentTypes = [AttachmentTypeCode.APPOINTMENT_PHOTO];
  const [damagePhotos, setDamagePhotos] = useState<CaseAttachment[]>([]);
  const [service, setService] = useState<Organization | null>(null);

  const loadService = async (selectedAppointment: AppointmentType) => {
    if (!selectedAppointment?.serviceId) {
      setService(null);
      return;
    }
    const organizations = await organizationService.GetUserOrganizationsByIds([
      selectedAppointment.serviceId
    ]);
    setService(organizations[0] ?? null);
  };

  const loadDamagePhotos = async () => {
    const res = await getAppointmentAttachments({ appointmentId: id, types: attachmentTypes });
    if (res.error || !res.data) {
      return;
    }
    setDamagePhotos(res.data);
  };

  const loadAppointmentStatusHistory = async (
    appointmentHistoryList?: Array<AppointmentHistoryType>
  ) => {
    if (!appointmentHistoryList) {
      return;
    }
    const appUserServiceInstance = new AppUserService();
    const futureAppointmentHistory: Array<AppointmentHistoryType> = [];
    appointmentHistoryList?.forEach(async (appointmentObj: AppointmentHistoryType) => {
      const user = await appUserServiceInstance.GetUserInfo(appointmentObj.userId);
      const arrayOfStatus: Array<AppointmentStatusType> = Object.values(statusList);
      const desiredStatus: AppointmentStatusType | undefined = arrayOfStatus.find(
        (status: any) => status.id === appointmentObj.appointmentStatusId
      );
      futureAppointmentHistory.push({
        ...appointmentObj,
        user: user,
        appointmentStatus: desiredStatus
      });
    });
    setAppointmentHistory(futureAppointmentHistory);
  };

  const getSelectedAppointment = async () => {
    const appointmentIdNum = id ? Number(id) : 0;
    if (!appointmentIdNum) {
      setIsLoading(false);
      return;
    }
    const request = {
      appointmentId: appointmentIdNum,
      ...(viewPartnerAppointments &&
        organizationId != null && { organizationOwnerId: organizationId })
    };
    const res = await getAppointmentDetailsRequest(request);
    const appointment = res.data as AppointmentType;
    if (res.error || !appointment) {
      setIsLoading(false);
      return;
    }
    loadAppointmentStatusHistory(appointment?.appointmentStatusHistories);
    setAppointment(appointment);
    loadDamagePhotos();
    await loadService(appointment);
    setIsLoading(false);
  };

  React.useEffect(() => {
    if (!statusList.INITIATA) {
      getStatusList(
        setIsLoading,
        (value: any) => {
          dispatch({
            type: AGENDA_SET_STATUS_LIST,
            payload: value
          });
        },
        enqueueSnackbar
      );
    } else if (id) {
      setIsLoading(true);
      getSelectedAppointment();
    }
  }, [statusList]);

  const fetchAppointment = async (id: number) => {
    if (!statusList.INITIATA) {
      getStatusList(
        setIsLoading,
        (value: any) => {
          dispatch({
            type: AGENDA_SET_STATUS_LIST,
            payload: value
          });
        },
        enqueueSnackbar
      );
    }

    setIsLoading(true);
    const request = {
      appointmentId: id,
      ...(viewPartnerAppointments &&
        organizationId != null && { organizationOwnerId: organizationId })
    };
    const res = await getAppointmentDetailsRequest(request);
    const appointment = res.data as AppointmentType;
    if (res.error || !appointment) {
      setIsLoading(false);
      return;
    }
    loadAppointmentStatusHistory(appointment?.appointmentStatusHistories);
    setAppointment(appointment);
    loadDamagePhotos();
    await loadService(appointment);
    setIsLoading(false);
  };

  useEffect(() => {
    apptIdRef.current = appointment?.id || null;
    console.log('appointment id set to ref', apptIdRef.current);
  }, [appointment?.id]);

  useEffect(() => {
    refreshService
      .AppointmentsToRefresh$()
      .pipe(takeUntil(destroy$))
      .subscribe(async (appointments: number[]) => {
        console.log('refresh received appointments', appointments);
        console.log('appointment id', apptIdRef.current);
        if (!apptIdRef.current!) {
          return;
        }

        if (appointments.includes(apptIdRef.current)) {
          fetchAppointment(apptIdRef.current);
        }
      });

    return () => {
      destroy$.next();
    };
  }, []);

  const onTabChange = (event: any, newValue: number) => {
    setSelectedTab(newValue);
  };

  const getActionButton = (appointment: AppointmentType | null) => {
    if (
      !(
        appointment &&
        ((appointment.vehicleId &&
          appointment?.appointmentStatus?.code === statusList.CONFIRMED?.code) ||
          appointment?.appointmentStatus?.code === statusList.PAYMENT_CONFIRMATION?.code ||
          appointment?.caseId)
      )
    ) {
      return null;
    }

    if (viewPartnerAppointments && appointment?.serviceId !== organizationId) {
      return appointment?.caseId ? (
        <Button
          aria-label="add"
          variant="outlined"
          color="primary"
          onClick={() => history.push('/cases/' + appointment.caseId, 'Case xx')}
        >
          <Folder style={{ marginRight: 4 }} />
          {translatorService.Tranlate('APPOINTMENT_VIEW_CASE', 'Vezi dosar')}
        </Button>
      ) : null;
    }

    return appointment?.caseId ? (
      <Button
        aria-label="add"
        variant="outlined"
        color="primary"
        onClick={() => history.push('/cases/' + appointment?.caseId, 'Case xx')}
      >
        <Folder style={{ marginRight: 4 }} />
        {translatorService.Tranlate('APPOINTMENT_VIEW_CASE', 'Vezi dosar')}
      </Button>
    ) : (
      <Button
        aria-label="add"
        variant="contained"
        color="secondary"
        onClick={() => setAppointmentId(appointment?.id)}
      >
        <CreateNewFolder style={{ color: 'white', marginRight: 4 }} />
        {translatorService.Tranlate('APPOINTMENT_CREATE_CASE', 'Creeaza dosar')}
      </Button>
    );
  };

  return (
    <>
      {isLoading ? (
        <Loader />
      ) : (
        <Fragment>
          <PageTitle
            titleHeading={`${translatorService.Tranlate(
              'APPOINTMENT_DETAILS',
              'Detalii programare'
            )}${
              appointment?.vehicle?.plateNumber ? ` - ${appointment?.vehicle?.plateNumber}` : ''
            } - ${appointment?.id}`}
            titleDescription={
              appointment?.appName ? (
                <span>
                  {`${translatorService.Tranlate(
                    'APPOINTMENT_DETAILS_APP_NAME',
                    'Aplicație sursă'
                  )}: `}
                  <span style={{ color: getAppNameColor(appointment.appName) }}>
                    {appointment.appName}
                  </span>
                </span>
              ) : (
                ''
              )
            }
            backButtonName={translatorService.Tranlate('BACK_TO_APPOINTMENTS_LIST', 'Programari')}
            onClickBack={() => {
              history.push('/agenda');
            }}
            actionButton={getActionButton(appointment ?? null)}
          />
          <Fragment>
            <Card>
              <Tabs
                TabIndicatorProps={{
                  style: {
                    height: '4px'
                  }
                }}
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                variant="fullWidth"
                centered={true}
                color="primary"
                onChange={onTabChange}
                aria-label="disabled tabs example"
              >
                <Tab label={translatorService.Tranlate('APPOINTMENT_DETAILS_TAB', 'Programare')} />
                <Tab label={translatorService.Tranlate('APPOINTMENT_DOCUMENTS_TAB', 'Documente')} />
                <Tab label={translatorService.Tranlate('APPOINTMENT_HISTORY_TAB', 'Istoric')} />
              </Tabs>
            </Card>
            <div>
              <TabPanel value={selectedTab} index={0}>
                <AppointmentDetails
                  appointment={appointment}
                  setIsLoading={setIsLoading}
                  getSelectedAppointment={getSelectedAppointment}
                  damagePhotos={damagePhotos}
                  service={service}
                />
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <AppointmentTabDocumentsForm appointmentId={id} />
              </TabPanel>
              <TabPanel value={selectedTab} index={2}>
                <AppointmentHistory appointmentHistory={appointmentHistory} isLoading={isLoading} />
              </TabPanel>
            </div>
          </Fragment>
          {appointmentId ? (
            <NewCaseAppointment
              appAppointmentId={appointmentId}
              close={() => setAppointmentId(null)}
            />
          ) : null}
        </Fragment>
      )}
    </>
  );
};

export default AppointmentPage;
